import React from 'react'
import ChatDrawer from '../chat/ChatDrawer'
import type { ChatMessage } from '../chat/types'
import ProposedThemesTray from './ProposedThemesTray'
import type { AutoCoderCode } from '../../types/gotypes'

interface ThemeAIAgentPaneProps {
  open: boolean
  onClose: () => void
  messages: ChatMessage[]
  loading: boolean
  error?: string
  hasFrame: boolean
  frameName?: string
  /** The saved frame's theme names, so the tray can warn when a proposal drops some. */
  frameThemeNames: string[]
  frameVersion?: number
  proposedThemes: AutoCoderCode[]
  onThemesChange: (themes: AutoCoderCode[]) => void
  saving: boolean
  sampleSize: number
  sampleSizes: readonly number[]
  poolSize: number
  onSampleSizeChange: (size: number) => void
  onSend: (model: string, input: string) => Promise<void>
  onSave: () => Promise<void>
}

// The code-frame agent as a side pane (Adam and Matt, 21 Sep: "the controls
// there relate to everything that's on the screen … the side panel just
// comes over"). It opens from any step; the page stays where it is.
const ThemeAIAgentPane: React.FC<ThemeAIAgentPaneProps> = ({
  open,
  onClose,
  messages,
  loading,
  error,
  hasFrame,
  frameName,
  frameThemeNames,
  frameVersion,
  proposedThemes,
  onThemesChange,
  saving,
  sampleSize,
  sampleSizes,
  poolSize,
  onSampleSizeChange,
  onSend,
  onSave,
}) => {
  const prompts = hasFrame
    ? [
        'Add any themes this frame is missing, keeping the ones it has',
        'Merge themes that overlap and keep the frame under 12 themes',
        'Write a one-line rule for when to assign each theme',
      ]
    : [
        'Suggest themes for these responses',
        'Suggest 8 broad themes',
        'Suggest themes, and a one-line rule for when to assign each',
      ]

  return (
    <ChatDrawer
      open={open}
      onClose={onClose}
      width={{ width: '40%', minWidth: 380, maxWidth: 720 }}
      title="Theme agent"
      scopeLabel={hasFrame && frameName ? frameName : undefined}
      messages={messages}
      onSendMessage={(model, input) => onSend(model, input)}
      isLoading={loading}
      loadingTitle="Reading the responses and working on the themes…"
      error={error}
      examplePrompts={messages.length === 0 ? prompts : undefined}
      autoTriggerExamplePrompts
      showAdvancedMode={false}
      placeholder={hasFrame ? 'Ask for a change to the themes…' : 'Describe the study, or ask for themes…'}
      inputHelper={
        <ProposedThemesTray
          proposedThemes={proposedThemes}
          onThemesChange={onThemesChange}
          frameThemeNames={frameThemeNames}
          hasFrame={hasFrame}
          nextVersion={(frameVersion || 0) + 1}
          saving={saving}
          loading={loading}
          refinements={messages.filter((message) => message.role === 'user').length}
          sampleSize={sampleSize}
          sampleSizes={sampleSizes}
          poolSize={poolSize}
          onSampleSizeChange={onSampleSizeChange}
          onSave={onSave}
        />
      }
    />
  )
}

export default ThemeAIAgentPane
